import { apiFetch, ApiError } from '../apiFetch'
import type {
  Customer,
  CustomerDetail,
  CustomerBuyer,
  CreateCustomerInput,
  UpdateCustomerInput,
  CreateBuyerInput,
  UpdateBuyerInput,
  CustomerFilter,
} from './types'

async function parseResponse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => null)
  if (!res.ok) {
    const err = data?.error
    const code = err?.code ?? 'HTTP_ERROR'
    const message = err?.message ?? `${fallback} (${res.status})`
    const details = err?.details
    throw new ApiError(code, message, res.status, details)
  }
  return data as T
}

export async function listCustomersApi(filter?: CustomerFilter): Promise<Customer[]> {
  const params = new URLSearchParams()
  if (filter?.q) {
    params.set('q', filter.q)
  }
  if (filter?.status && filter.status !== 'all') {
    params.set('status', filter.status)
  }
  const query = params.toString() ? `?${params.toString()}` : ''
  const res = await apiFetch(`/api/customers${query}`)
  return parseResponse<Customer[]>(res, 'Không thể tải danh sách khách hàng')
}

export async function getCustomerApi(id: string): Promise<CustomerDetail | null> {
  const res = await apiFetch(`/api/customers/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  return parseResponse<CustomerDetail>(res, 'Không thể tải thông tin khách hàng')
}

export async function createCustomerApi(input: CreateCustomerInput): Promise<Customer> {
  const res = await apiFetch('/api/customers', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return parseResponse<Customer>(res, 'Không thể tạo khách hàng')
}

export async function patchCustomerApi(
  id: string,
  patch: UpdateCustomerInput,
): Promise<Customer> {
  const res = await apiFetch(`/api/customers/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  return parseResponse<Customer>(res, 'Không thể cập nhật khách hàng')
}

export async function createBuyerApi(
  customerId: string,
  input: CreateBuyerInput,
): Promise<CustomerBuyer> {
  const res = await apiFetch(`/api/customers/${encodeURIComponent(customerId)}/buyers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return parseResponse<CustomerBuyer>(res, 'Không thể thêm người mua')
}

export async function patchBuyerApi(
  customerId: string,
  buyerId: string,
  patch: UpdateBuyerInput,
): Promise<CustomerBuyer> {
  const res = await apiFetch(
    `/api/customers/${encodeURIComponent(customerId)}/buyers/${encodeURIComponent(buyerId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  return parseResponse<CustomerBuyer>(res, 'Không thể cập nhật người mua')
}
